import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getEnrichmentSnapshot } from '@/lib/pageDataSnapshot';

export interface OnetEnrichment {
  soc_title: string | null;
  job_zone: number | null;
  education_summary: string | null;
  top_skills: string[];
  top_tasks: string[];
  related_socs: string[];
}

export interface ProjectionsEnrichment {
  base_year: number | null;
  proj_year: number | null;
  base_employment: number | null;
  proj_employment: number | null;
  pct_change: number | null;
  annual_openings: number | null;
  outlook_band: 'growing-fast' | 'growing' | 'stable' | 'declining' | null;
}

export interface LayoffEvent {
  company: string;
  industry: string | null;
  location: string | null;
  state_code: string | null;
  affected: number | null;
  event_date: string;
  source_url: string | null;
  naics_code?: string | null;
  /** True when the event is in the page's state (state pages rank these first). */
  is_state_match?: boolean;
}

export interface OccupationScores {
  pay_score: number | null;
  growth_score: number | null;
  no_degree_score: number | null;
  ai_safety_score: number | null;
  affordability_score: number | null;
}

export interface OccupationEnrichment {
  soc: string;
  onet: OnetEnrichment | null;
  projections: ProjectionsEnrichment | null;
  scores: OccupationScores | null;
  layoffs: LayoffEvent[];
  has_enriched_content: boolean;
}

/**
 * Fetches the unique, enriched content for an occupation (O*NET skills/tasks,
 * BLS growth outlook, layoffs). Free for everyone. Degrades gracefully: when
 * no enrichment exists yet, `has_enriched_content` is false. Indexation must
 * NOT depend on this flag (see src/lib/seoIndexing.ts). Kept so the page can
 * drop to noindex and hide empty modules.
 */
export const useOccupationEnrichment = (soc?: string, stateCode?: string) => {
  return useQuery<OccupationEnrichment>({
    queryKey: ['occupation-enrichment', soc, stateCode],
    enabled: !!soc,
    staleTime: 1000 * 60 * 60 * 6,
    gcTime: 1000 * 60 * 60 * 24,
    queryFn: async () => {
      // Build-time snapshot: fast hydration for prerender (O*NET, projections, scores)
      if (soc) {
        const snapshot = await getEnrichmentSnapshot(soc);
        if (snapshot) return snapshot as OccupationEnrichment;
      }

      const { data, error } = await supabase.functions.invoke('api-occupation-enrichment', {
        body: { soc, state_code: stateCode },
      });
      if (error) throw error;
      return data as OccupationEnrichment;
    },
  });
};
